import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import MyQuotes from './customer/MyQuotes';
import MyInvoices from './customer/MyInvoices';
import MyJobs from './customer/MyJobs';
import MyRequests from './customer/MyRequests';
import {
  LayoutDashboard,
  Package,
  Calendar,
  CreditCard,
  FileText,
  MessageSquare,
  LogOut,
  ShoppingCart,
  Wrench,
  Clock,
  DollarSign,
  User,
  MapPin,
  Receipt
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../utils/dateUtils';

export default function CustomerDashboard({ user, onLogout }) {
  const [customerData, setCustomerData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [customerPackages, setCustomerPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [customerProperties, setCustomerProperties] = useState([]);
  const [leadForms, setLeadForms] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const [bookingForm, setBookingForm] = useState({
    property_id: '',
    service_required_date: '',
    services_required: '',
    additional_notes: '',
    consent_given: false
  });

  const [feedbackForm, setFeedbackForm] = useState({
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    fetchCustomerData();
  }, []);

  const fetchCustomerData = async () => {
    try {
      const [customersRes, packagesRes, servicesRes, leadFormsRes] = await Promise.all([
        axiosInstance.get('/customers'),
        axiosInstance.get('/packages'),
        axiosInstance.get('/services'),
        axiosInstance.get('/lead-forms')
      ]);
      
      const customer = customersRes.data.find(c => c.email === user.email);
      setCustomerData(customer);
      setPackages(packagesRes.data);
      setServices(servicesRes.data);
      setLeadForms(leadFormsRes.data || []);

      if (customer) {
        const [cpRes, jobsRes, paymentsRes, propsRes] = await Promise.all([
          axiosInstance.get(`/customers/${customer.id}/packages`),
          axiosInstance.get(`/jobs?customer_id=${customer.id}`),
          axiosInstance.get(`/payments?customer_id=${customer.id}`),
          axiosInstance.get(`/customers/${customer.id}/properties`)
        ]);
        setCustomerPackages(cpRes.data);
        setJobs(jobsRes.data);
        setPayments(paymentsRes.data);
        setCustomerProperties(propsRes.data || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchasePackage = async (packageId) => {
    try {
      await axiosInstance.post('/packages/purchase', {
        package_id: packageId,
        customer_id: customerData.id
      });
      toast.success('Package purchased successfully!');
      setShowPurchaseDialog(false);
      fetchCustomerData();
    } catch (error) {
      toast.error('Failed to purchase package');
    }
  };

  const handleBookService = async (e) => {
    e.preventDefault();
    if (!bookingForm.consent_given) {
      toast.error('Please provide consent to continue');
      return;
    }
    if (!bookingForm.services_required.trim()) {
      toast.error('Please describe the service required');
      return;
    }

    try {
      // Find the selected property to get address details
      const selectedProp = customerProperties.find(p => p.id === bookingForm.property_id);
      const address = selectedProp || {};

      // Use the first available lead form, or a default form_id
      const formId = leadForms.length > 0
        ? (leadForms.find(f => f.form_name?.includes('Service Request'))?.id || leadForms[0].id)
        : 'customer-portal-booking';

      await axiosInstance.post('/leads/public-submit', {
        form_id: formId,
        full_name: customerData.name,
        email: customerData.email,
        phone: customerData.phone || '',
        property_address: {
          address_line: address.address_line || address.full_address || customerData.address || '',
          city: address.city || '',
          state: address.state || '',
          pincode: address.pincode || ''
        },
        service_required_date: bookingForm.service_required_date,
        services_required: bookingForm.services_required.split(',').map(s => s.trim()).filter(Boolean),
        additional_notes: bookingForm.additional_notes,
        consent_given: bookingForm.consent_given
      });

      toast.success('Service request submitted successfully!');
      setShowBookingDialog(false);
      setBookingForm({ property_id: '', service_required_date: '', services_required: '', additional_notes: '', consent_given: false });
      fetchCustomerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;

    try {
      await axiosInstance.post('/feedback', {
        job_id: selectedJob.id,
        customer_id: customerData.id,
        handyman_id: selectedJob.handyman_id,
        rating: parseInt(feedbackForm.rating),
        comment: feedbackForm.comment
      });
      toast.success('Feedback submitted successfully!');
      setShowFeedbackDialog(false);
      setSelectedJob(null);
      setFeedbackForm({ rating: 5, comment: '' });
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/customer' },
    { icon: FileText, label: 'My Requests', path: '/customer/requests' },
    { icon: FileText, label: 'My Quotes', path: '/customer/quotes' },
    { icon: Package, label: 'My Packages', path: '/customer/packages' },
    { icon: Calendar, label: 'My Jobs', path: '/customer/jobs' },
    { icon: Receipt, label: 'My Invoices', path: '/customer/invoices' },
    { icon: CreditCard, label: 'Payments', path: '/customer/payments' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      assigned: 'bg-purple-500',
      on_the_way: 'bg-indigo-500',
      in_progress: 'bg-orange-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-teal-50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white shadow-xl z-10 border-r border-gray-200 flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Redefyne
          </h1>
          <p className="text-sm text-gray-500 mt-1">Customer Portal</p>
        </div>

        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-teal-100 text-teal-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-gray-900">{user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-gradient-to-r from-teal-600 to-blue-600 text-white p-4 z-50 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Redefyne</h1>
            <p className="text-sm opacity-90">{user.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/20">
            <LogOut size={18} />
          </Button>
        </div>
      </div>

      {/* Main Content - Responsive margin */}
      <main className="md:ml-64 p-4 md:p-8 pt-20 md:pt-8 pb-24 md:pb-8">
        <Routes>
          <Route
            path="/"
            element={
              <div data-testid="customer-dashboard">
                <div className="mb-6 md:mb-8">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Welcome, {user.name}!
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base">Manage your service bookings and packages</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                  <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm md:text-lg">Active Packages</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl md:text-4xl font-bold" data-testid="active-packages-count">
                        {customerPackages.filter(p => p.is_active).length}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm md:text-lg">Total Hours</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl md:text-4xl font-bold" data-testid="total-hours">
                        {customerPackages.reduce((sum, p) => sum + p.remaining_hours, 0).toFixed(1)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm md:text-lg">Total Jobs</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl md:text-4xl font-bold" data-testid="total-jobs">{jobs.length}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                  <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                    <CardHeader>
                      <CardTitle className="text-base md:text-lg">Purchase Package</CardTitle>
                      <CardDescription className="text-xs md:text-sm">Buy hours for services</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
                        <DialogTrigger asChild>
                          <Button className="w-full" data-testid="purchase-package-btn">
                            <ShoppingCart size={16} className="mr-2" />
                            Browse Packages
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Available Packages</DialogTitle>
                            <DialogDescription>Choose a package that suits your needs</DialogDescription>
                          </DialogHeader>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {packages.map((pkg) => (
                              <Card key={pkg.id} className="border-2">
                                <CardHeader>
                                  <CardTitle className="text-lg md:text-xl">{pkg.name}</CardTitle>
                                  <CardDescription className="text-xs md:text-sm">{pkg.description}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                  <div className="text-2xl md:text-3xl font-bold text-teal-600">${pkg.price}</div>
                                  <div className="space-y-1 text-xs md:text-sm text-gray-600">
                                    <div className="flex items-center gap-2">
                                      <Clock size={16} />
                                      <span>{pkg.hours} Hours</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Calendar size={16} />
                                      <span>Valid for {pkg.validity_months} months</span>
                                    </div>
                                  </div>
                                  <Button
                                    className="w-full"
                                    onClick={() => handlePurchasePackage(pkg.id)}
                                    data-testid={`buy-package-${pkg.id}`}
                                  >
                                    Purchase Now
                                  </Button>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </CardContent>
                  </Card>

                  <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                    <CardHeader>
                      <CardTitle className="text-base md:text-lg">Book Service</CardTitle>
                      <CardDescription className="text-xs md:text-sm">Schedule a new service</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Dialog open={showBookingDialog} onOpenChange={(open) => {
                          setShowBookingDialog(open);
                          if (open) {
                            const primary = customerProperties.find(p => p.is_primary);
                            setBookingForm(prev => ({
                              ...prev,
                              property_id: primary?.id || (customerProperties[0]?.id || '')
                            }));
                          }
                        }}>
                        <DialogTrigger asChild>
                          <Button className="w-full" data-testid="book-service-btn">
                            <Wrench size={16} className="mr-2" />
                            Book Now
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Submit Service Request</DialogTitle>
                            <DialogDescription>Fill in the details and we'll get back to you</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleBookService} className="space-y-5">
                            {/* Pre-filled Customer Info */}
                            <div className="p-3 bg-gray-50 rounded-lg border space-y-1">
                              <p className="text-sm font-medium text-gray-800">{customerData?.name}</p>
                              <p className="text-xs text-gray-500">{customerData?.email} {customerData?.phone ? `| ${customerData.phone}` : ''}</p>
                            </div>

                            {/* Property Selection */}
                            {customerProperties.length > 0 && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                  <MapPin size={14} className="text-teal-600" />
                                  Service Property <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={bookingForm.property_id}
                                  onValueChange={(value) => setBookingForm({ ...bookingForm, property_id: value })}
                                >
                                  <SelectTrigger data-testid="booking-property-select">
                                    <SelectValue placeholder="Select a property" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {customerProperties.map((prop) => (
                                      <SelectItem key={prop.id} value={prop.id}>
                                        {prop.property_name} — {prop.full_address || prop.address_line}
                                        {prop.is_primary ? ' (Primary)' : ''}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {/* Service Details Section */}
                            <div className="space-y-4 pt-1">
                              <div className="flex items-center gap-2 border-b pb-2">
                                <Wrench size={16} className="text-teal-600" />
                                <span className="font-semibold text-sm text-gray-800">Service Details</span>
                              </div>

                              <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                  <Calendar size={14} className="text-teal-600" />
                                  Service Required Date <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                  type="date"
                                  value={bookingForm.service_required_date}
                                  onChange={(e) => setBookingForm({ ...bookingForm, service_required_date: e.target.value })}
                                  data-testid="booking-service-date"
                                  required
                                />
                              </div>

                              <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                  <Wrench size={14} className="text-teal-600" />
                                  Service Required <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                  value={bookingForm.services_required}
                                  onChange={(e) => setBookingForm({ ...bookingForm, services_required: e.target.value })}
                                  placeholder="e.g., Plumbing repair, Painting, AC installation..."
                                  data-testid="booking-service-required"
                                  required
                                />
                              </div>

                              <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                  <FileText size={14} className="text-teal-600" />
                                  Additional Notes (Optional)
                                </Label>
                                <Textarea
                                  value={bookingForm.additional_notes}
                                  onChange={(e) => setBookingForm({ ...bookingForm, additional_notes: e.target.value })}
                                  placeholder="Please provide any additional details about your service request..."
                                  rows={3}
                                  data-testid="booking-notes"
                                />
                              </div>
                            </div>

                            {/* Consent Checkbox */}
                            <div className="p-3 bg-gray-50 rounded-lg border">
                              <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={bookingForm.consent_given}
                                  onChange={(e) => setBookingForm({ ...bookingForm, consent_given: e.target.checked })}
                                  className="mt-1 accent-teal-600"
                                  data-testid="booking-consent-checkbox"
                                />
                                <span className="text-xs text-gray-600 leading-relaxed">
                                  I consent & authorize Redefyne to contact me via Email, WhatsApp, and phone calls for service updates, transaction notifications, promotional communications, and important information related to my request. <span className="text-red-500">*</span>
                                </span>
                              </label>
                            </div>

                            <Button
                              type="submit"
                              className="w-full bg-teal-600 hover:bg-teal-700"
                              disabled={!bookingForm.consent_given || !bookingForm.services_required || !bookingForm.service_required_date}
                              data-testid="submit-service-request-btn"
                            >
                              Submit Service Request
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Jobs */}
                <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-base md:text-lg">Recent Jobs</CardTitle>
                    <CardDescription className="text-xs md:text-sm">Your latest service bookings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {jobs.slice(0, 5).map((job) => (
                      <div
                        key={job.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 border-b last:border-0 gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                        data-testid={`job-item-${job.id}`}
                        onClick={() => navigate('/customer/jobs')}
                      >
                        <div>
                          <div className="font-medium text-sm md:text-base">{job.service_name}</div>
                          <div className="text-xs md:text-sm text-gray-500">{job.address}</div>
                        </div>
                        <div className="flex items-center gap-2 md:gap-3">
                          <Badge className={`${getStatusColor(job.status)} text-white text-xs`}>
                            {job.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                          {job.status === 'completed' && job.handyman_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedJob(job);
                                setShowFeedbackDialog(true);
                              }}
                              data-testid={`feedback-btn-${job.id}`}
                              className="text-xs"
                            >
                              <MessageSquare size={14} className="mr-1" />
                              Feedback
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {jobs.length === 0 && (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No jobs yet. Book your first service!
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Feedback Dialog */}
                <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Submit Feedback</DialogTitle>
                      <DialogDescription>Rate your service experience</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmitFeedback} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Rating (1-5)</Label>
                        <Select
                          value={feedbackForm.rating.toString()}
                          onValueChange={(value) => setFeedbackForm({ ...feedbackForm, rating: parseInt(value) })}
                        >
                          <SelectTrigger data-testid="rating-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <SelectItem key={rating} value={rating.toString()}>
                                {rating} Star{rating > 1 ? 's' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Comment</Label>
                        <Textarea
                          value={feedbackForm.comment}
                          onChange={(e) => setFeedbackForm({ ...feedbackForm, comment: e.target.value })}
                          placeholder="Share your experience..."
                          data-testid="feedback-comment"
                        />
                      </div>
                      <Button type="submit" className="w-full" data-testid="submit-feedback">
                        Submit Feedback
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            }
          />

          <Route
            path="/requests"
            element={<MyRequests customerData={customerData} userEmail={user.email} />}
          />

          <Route
            path="/packages"
            element={
              <div data-testid="customer-packages">
                <div className="mb-6 md:mb-8">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    My Packages
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base">View your active service packages</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {customerPackages.map((pkg) => (
                    <Card key={pkg.id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base md:text-lg">{pkg.package_name}</CardTitle>
                          <Badge className={pkg.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                            {pkg.is_active ? 'Active' : 'Expired'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs md:text-sm text-gray-500">Total Hours</div>
                            <div className="text-xl md:text-2xl font-bold">{pkg.total_hours}h</div>
                          </div>
                          <div>
                            <div className="text-xs md:text-sm text-gray-500">Remaining</div>
                            <div className="text-xl md:text-2xl font-bold text-teal-600">{pkg.remaining_hours.toFixed(1)}h</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-teal-600 h-3 rounded-full transition-all"
                            style={{ width: `${(pkg.remaining_hours / pkg.total_hours) * 100}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500">
                          Expires: {formatDate(pkg.expiry_date)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            }
          />

          <Route
            path="/jobs"
            element={<MyJobs customerData={customerData} />}
          />

          <Route
            path="/payments"
            element={
              <div data-testid="customer-payments">
                <div className="mb-6 md:mb-8">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Payment History
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base">View your transaction history</p>
                </div>
                <div className="space-y-4">
                  {payments.map((payment) => (
                    <Card key={payment.id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 md:gap-4">
                            <div className="p-2 md:p-3 bg-teal-100 rounded-full">
                              <CreditCard className="text-teal-600" size={20} />
                            </div>
                            <div>
                              <div className="font-medium text-sm md:text-base">Payment #{payment.id.slice(0, 8)}</div>
                              <div className="text-xs md:text-sm text-gray-500">
                                {formatDate(payment.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl md:text-2xl font-bold">${payment.amount.toFixed(2)}</div>
                            <Badge className="bg-green-500 text-white mt-1 text-xs">
                              {payment.payment_status.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            }
          />
          <Route path="/quotes" element={<MyQuotes customerEmail={user.email} />} />
          <Route path="/invoices" element={<MyInvoices customerEmail={user.email} />} />
        </Routes>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 safe-area-inset-bottom">
        <div className="grid grid-cols-5 gap-1 p-2">
          <Link
            to="/customer"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/customer' ? 'bg-teal-100 text-teal-700' : 'text-gray-600'
            }`}
          >
            <LayoutDashboard size={20} />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link
            to="/customer/requests"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/customer/requests' ? 'bg-teal-100 text-teal-700' : 'text-gray-600'
            }`}
          >
            <FileText size={20} />
            <span className="text-xs mt-1">Requests</span>
          </Link>
          <Link
            to="/customer/quotes"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/customer/quotes' ? 'bg-teal-100 text-teal-700' : 'text-gray-600'
            }`}
          >
            <FileText size={20} />
            <span className="text-xs mt-1">Quotes</span>
          </Link>
          <Link
            to="/customer/jobs"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/customer/jobs' ? 'bg-teal-100 text-teal-700' : 'text-gray-600'
            }`}
          >
            <Calendar size={20} />
            <span className="text-xs mt-1">Jobs</span>
          </Link>
          <Link
            to="/customer/invoices"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/customer/invoices' ? 'bg-teal-100 text-teal-700' : 'text-gray-600'
            }`}
          >
            <Receipt size={20} />
            <span className="text-xs mt-1">Invoices</span>
          </Link>
        </div>
      </div>
    </div>
  );
}